// `dispute.status-changed.v1` — emitted by dispute-backend (Python) on every
// dispute status transition (Stripe + Finix webhooks), AFTER the transaction
// row's `state` has been updated (DISPUTED_OPEN / DISPUTED_CLOSED /
// WARNING_DISPUTED, or reverted to the prior state on a WON dispute).
//
// Producer: dispute-backend (`Source: 'charitystack.disputes'`,
//   functions/product_analytics_events.py::publish_dispute_status_changed).
//   Originally built as a product-analytics event; PE-1768 makes it a consumed
//   domain event. The producer `_compact()`s the detail — null fields are
//   DROPPED, hence the optionals below. `originIdempotencyKey` is
//   `dispute.status:<processor>:<disputeId>:<status>` (unique per transition,
//   so opened AND won/lost each trigger their own recompute — exactly right:
//   both transitions change whether the gift counts).
// Consumer: marketing-backend — the revenue-reversal bridge → the
//   `revenue-reversal` Inngest workflow (same pipe as refund.issued.v1).
// detailType in EventBridge: `dispute.status-changed.v1` (Commitment 6).
//
// Status: active
//
// Parsed NON-strictly (no .strict()) — additive producer evolution must not
// break in-flight events.
import { z } from 'zod';

import { eventEnvelopeSchema } from './base.js';

export const disputeStatusChangedV1Schema = eventEnvelopeSchema.extend({
  // The disputed donation's per-charge transaction id — the consumer's only
  // required handle.
  transactionId: z.string().min(1),
  disputeId: z.string().min(1),
  // Processor-native status string (Stripe: needs_response/won/lost/…;
  // Finix: INQUIRY/PENDING/WON/LOST). Plain string: the consumer recomputes
  // from the transaction's resulting `state` regardless of which transition
  // fired.
  status: z.string().min(1),
  amountCents: z.number().int().optional(),
  reason: z.string().optional(),
  responseStatus: z.string().optional(),
  paymentProcessor: z.string().optional(),
});

export type DisputeStatusChangedV1 = z.infer<typeof disputeStatusChangedV1Schema>;

export const DISPUTE_STATUS_CHANGED_V1_DETAIL_TYPE = 'dispute.status-changed.v1' as const;
export const DISPUTE_STATUS_CHANGED_V1_SOURCE = 'charitystack.disputes' as const;
